import { StripeMetadataInput, SuccessData } from '../../components/VaultForm';
import { StripeEnv } from '../../util/enums';
import { FinancialAccountNode, OrgNode } from '../orgs/interfaces';
import { vaultRequest } from './request';

const ORG_CONFIRMATION_QUERY = `mutation VaultOrgConfirmationSubmit(
$gateway: VaultFinancialProvider!,
$orgData: VaultFormKybInput!,
$rawRepresentativeData: VaultRepresentativeCreateInput!,
$submittedOnYourBehalf: Boolean!,
$stripeData: VaultFormStripeInput,
$bankAccountPayableOrgId: ID
$fundraiserId: String
$fundraiserSlug: String
$productDescription: String
$rawBeneficialOwners: [VaultBeneficialOwnerCreateInput!]) {
  vaultOrgConfirmationSubmit(
  gateway: $gateway,
  orgData: $orgData,
  primaryRepresentative: $rawRepresentativeData,
  submittedOnYourBehalf: $submittedOnYourBehalf,
  stripeData: $stripeData,
  bankAccountPayableOrgId: $bankAccountPayableOrgId
  fundraiserId: $fundraiserId
  fundraiserSlug: $fundraiserSlug
  productDescription: $productDescription
  beneficialOwners: $rawBeneficialOwners
  ) {
    errors {
      details
      field
      fieldMessage
      message
      retryable
      type
    }
    success
    successData {
      beneficialOwners {
        kycId
        stripePersonId
        userId
      }
      kybData {
        financialAccountId
        kybId
        metadata {
          activityType
          entity
          fundraiserId
          paymentType
          transactionId
          fundraiserId_legacy
        }
        orgId
        stripeConnectAccountId
      }
      payableOrgNode {
        beneficialOwners {
          kycId
          stripePersonId
          userId
        }
        kybData {
          financialAccountId
          kybId
          metadata {
            activityType
            entity
            fundraiserId
            paymentType
            transactionId
            fundraiserId_legacy
          }
          orgId
          stripeConnectAccountId
        }
        representative {
          kycId
          stripePersonId
          userId
        }
      }
      representative {
        kycId
        stripePersonId
        userId
      }
      stripeEnv
    }
  }
}`;

export const confirmOrg = async ({
  provider,
  financialAccountNode,
  kyb,
  org,
  representative,
  primaryKycStripePerson,
  currentUserId,
  submittedOnYourBehalf,
  metadata,
  statementDescriptor,
  stripeEnv,
  bankAccountPayableOrgId,
  fundraiserId,
  fundraiserSlug,
  productDescription,
  owners,
}: {
  provider: VaultProvider;
  financialAccountNode?: FinancialAccountNode;
  kyb: Kyb;
  org: OrgNode;
  representative: Kyc;
  primaryKycStripePerson?: string;
  currentUserId: string;
  submittedOnYourBehalf: boolean;
  metadata?: StripeMetadataInput;
  statementDescriptor?: string;
  stripeEnv: StripeEnv;
  bankAccountPayableOrgId?: string;
  fundraiserId?: string | null;
  fundraiserSlug?: string | null;
  productDescription?: string | null;
  owners: Owner[];
}): Promise<SuccessData> => {
  if (!financialAccountNode?.acctId) {
    throw new Error('financialAccountNode.acctId required');
  }
  const confirmOrgRes = await vaultRequest(ORG_CONFIRMATION_QUERY, {
    gateway: provider,
    orgData: {
      accountId: financialAccountNode.acctId,
      financialAccountNodeId: financialAccountNode.finAcctId,
      kybId: kyb.id,
      orgId: org.id,
    },
    rawRepresentativeData: {
      kycId: representative.id,
      stripePersonId: primaryKycStripePerson,
      userId: currentUserId,
    },
    submittedOnYourBehalf,
    stripeData: {
      metadata,
      statementDescriptor,
      stripeEnv,
    },
    bankAccountPayableOrgId,
    fundraiserId,
    fundraiserSlug,
    productDescription,
    rawBeneficialOwners: owners.map((o) => ({
      kycId: o.kycId,
    })),
  });

  const confirmOrgResJson = await confirmOrgRes.json();
  if (confirmOrgResJson.errors && confirmOrgResJson.errors.length > 0) {
    throw new Error(`Could not confirm org`);
  }
  return confirmOrgResJson?.data?.vaultOrgConfirmationSubmit?.successData;
};
